/**
 * Selectors — every read the shell makes, off the view the host last gave it.
 *
 * @packageDocumentation
 */

import type { GameDescription, PendingQuestionView, PortReport, SessionView } from '@orpheus/api-contracts';
import type { RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { GameStoreState } from './create-game-store.js';

const EMPTY_TRANSCRIPT: readonly RuntimeTranscriptEntry[] = [];
const EMPTY_HISTORY: readonly string[] = [];

/** The view the host last handed back, once a session is open. */
export const selectSessionView = (state: GameStoreState): SessionView | undefined => state.session.view;

/** The game around the session, once it is open. */
export const selectGame = (state: GameStoreState): GameDescription | undefined => state.session.game;

/** The seed the session runs on, once it is open. */
export const selectSessionSeed = (state: GameStoreState): number | undefined => state.session.seed;

/** The port the shell is asking about (D163), or nothing. */
export const selectPendingPort = (state: GameStoreState): PortReport | undefined => state.session.pendingPort;

export const selectCommandHistory = (state: GameStoreState): readonly string[] => state.session.view?.commandHistory ?? EMPTY_HISTORY;

export const selectHistoryBrowseValue = (state: GameStoreState): string | undefined => {
	const { historyCursorIndex } = state.commandUi;

	return historyCursorIndex === undefined ? undefined : selectCommandHistory(state)[historyCursorIndex];
};

export const selectHistoryCursorIndex = (state: GameStoreState): number | undefined => state.commandUi.historyCursorIndex;

export const selectInputValue = (state: GameStoreState): string => state.commandUi.inputValue;

export const selectIsSessionReady = (state: GameStoreState): boolean => state.session.sessionStatus === 'ready';

/** Whether a terminal ending has closed the story. */
export const selectIsSessionEnded = (state: GameStoreState): boolean => state.session.view?.isEnded ?? false;

export const selectTranscriptEntries = (state: GameStoreState): readonly RuntimeTranscriptEntry[] =>
	state.session.view?.transcript ?? EMPTY_TRANSCRIPT;

export const selectPendingQuestion = (state: GameStoreState): PendingQuestionView | undefined => state.session.view?.pendingQuestion;

export const selectTurnCount = (state: GameStoreState): number => state.session.view?.turnCount ?? 0;
